import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { makeStyles, styled } from "@mui/styles";
import { KeyConstant, LangConstant, SystemConstant, AppConstant } from "const";
import { isJSONString, toCamel, StorageUtil, convertString2JSON, uuid, toSnake, isE2E } from "utils";
import { isEqual } from "lodash";
import { getNSLang } from "utils/lang.utils";
import { Replay, Error, Refresh } from "@mui/icons-material";
import MessengerPeople from "./MessengerPeople";
import FilePreview from "./FilePreview";
import ImageMessage from "./MediaPreview/ImageMessage";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { getInteractor } from "services/local.service";
import ThreadFlow from "./ThreadFlow";
import ReactionFlow from "./ReactionFlow";
import ChatAction from "./ChatAction";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useCleanUpEffect } from "hooks";
import { getLatestMessage } from "utils/view.utils";
import { ConversationActions, ConversationSelectors, SystemSelectors, SystemActions } from "redux-store";
import DeleteMessage from "./DeleteMessage";
import QuoteMessage from "./QuoteMessage";
import { LockIcon } from "components/icons";
import { checkTriggerMessageUI } from "sagas/saga.helper";

const EncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.EncryptTypeProtocol;

const ChatItem = ({ data, isThreadMode, isSearchMode, ...otherProps }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);

  const isFocused = useSelector(SystemSelectors.isFocused);
  const updateMessageStatus = useSelector(state => state.conversationRedux.updateMessageStatus, shallowEqual);
  const updateThreadIds = useSelector(ConversationSelectors.getUpdateThreadIds);
  const { isMounted } = useCleanUpEffect();

  const isMine = data.senderId === accountId;
  const isSentByThisDevice = isMine && data.senderDeviceId === deviceId;
  const isSending = isSentByThisDevice && isMine && !Boolean(data.modified);
  const isHiddenMessage = data.isDeleteOnlyMe && data.state === SystemConstant.STATE.inactive;
  const isViewMode = false === Boolean(isThreadMode || isSearchMode);

  const [message, setMessage] = useState({});
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [reactionList, setReactionList] = useState([]);
  const [threadInfo, setThreadInfo] = useState({});
  const [hasNewReply, setHasNewReply] = useState(false);
  const [messageOption, setMessageOption] = useState({});
  const [isDecryptFailed, setIsDecryptFailed] = useState(false);

  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);

  const [isFailedSending, isShowMessageDetail, isSendErr, sendingMsgStatus] = useMemo(() => {
    const isFailedSending = !isSending && message.state === SystemConstant.STATE.inactive && isSentByThisDevice;
    const isShowMessageDetail = !isSending && !isFailedSending && message.state === SystemConstant.STATE.active;
    const isSendErr = !isSending && isSentByThisDevice && message.status === SystemConstant.MESSAGE_STATUS.failed;
    let sendingMsgStatus = SENDING_STATUS.success;
    switch (true) {
      case Boolean(isSending):
        sendingMsgStatus = SENDING_STATUS.sending;
        break;

      case Boolean(isFailedSending):
        sendingMsgStatus = SENDING_STATUS.failed;
        break;
      case Boolean(isSendErr):
        sendingMsgStatus = SENDING_STATUS.sendErr;
        break;
      default:
        break;
    }

    return [isFailedSending, isShowMessageDetail, isSendErr, sendingMsgStatus];
  }, [message]);

  const handleEditMode = () => {
    if (isSearchMode || isHiddenMessage) return;

    setIsEditing(true);
  };

  const handleHoverChat = hover => () => {
    if (isSearchMode || isHiddenMessage) return;
    setIsHovered(hover);
  };

  const handleCloseEdit = () => {
    setIsEditing(false);
  };

  const handleJump2Thread = () => {
    dispatch(
      ConversationActions.setSelectGroupId({
        selectedGroupId: message.groupId,
        threadingId: message.threadId,
      }),
    );
  };

  const handleResendMessage = async () => {
    const groupDetail = await getInteractor(prefixKey).LocalGroupService.get(message.groupId);
    const memberIdArray = groupDetail.groupMembers.map(member => member.id) || [];
    const deviceList = await getInteractor(prefixKey).LocalDeviceService.find({
      account_id: memberIdArray,
      state: SystemConstant.STATE.active,
    });

    // case 1: not encrypt and not send to server
    if (
      message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.encrypting ||
      message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.encryptFailed
    ) {
      await handleResendMessageEncryptFailed(message, groupDetail);
    } else if (
      message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.encryptFileFailed ||
      message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.fileSendFailed ||
      message.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.fileSending
    ) {
      await handleResendSendFileFailed(message, groupDetail);
    }
    // case encrypt and not send to server
    else if (message.status === SystemConstant.MESSAGE_STATUS.failed) {
      await handleResendMsgEncryptNotResend(message, groupDetail, deviceList);
    }
  };

  const handleEncryptResend = message => {
    const updateMessage = {
      modified: Date.now(),
      messageEncryptStatus: SystemConstant.MESSAGE_ENCRYPT_STATUS.encryptResend,
      status: SystemConstant.MESSAGE_STATUS.send,
    };
    getInteractor(prefixKey).LocalMessageService.updateMessage(message.id, toSnake(updateMessage));
    if (checkTriggerMessageUI(prefixKey, message)) {
      dispatch(ConversationActions.receivedRemoteMessage(toSnake({ ...message, ...updateMessage })));
    }
    return { ...message, ...updateMessage };
  };

  const handleResendMessageEncryptFailed = async (message, groupDetail) => {
    const messageEncrypts = await getInteractor(prefixKey).LocalMessageEncryptService.findBySourceId(message.sourceId);
    if (isE2E(message.sendType, groupDetail.groupType)) {
      if (messageEncrypts.length > 0) {
        throw new Error("Has message encrypts");
      }
    }
    message = handleEncryptResend(message);
    dispatch(
      ConversationActions.sendMessage(
        {
          groupId: message.groupId,
          sendType: message.sendType,
          content: message.content,
          parentId: message.parentId,
          branchId: message.branchId,
          mentionIdsArr: JSON.stringify(message.mentions),
          threadId: message.threadId,
          currentMessage: toSnake(message),
          forward: message.forward,
        },
        prefixKey,
      ),
    );
  };

  const handleResendSendFileFailed = async (message, groupDetail) => {
    const multipleFile = convertString2JSON(message.content, {}).multiple_file || [];
    for (let index = 0; index < multipleFile.length; index++) {
      const file = multipleFile[index];
      const fileRecord = await getInteractor(prefixKey).LocalFileService.get(file.attachment_id);
      if (fileRecord && fileRecord?.status === SystemConstant.UPLOAD_STATUS.success) {
        continue;
      }
      await getInteractor(prefixKey).LocalFileService.update(
        { status: SystemConstant.UPLOAD_STATUS.inProgress },
        { id: file.attachment_id },
      );
    }
    message = handleEncryptResend(message);
    dispatch(
      ConversationActions.sendFileMessage(
        {
          groupId: groupDetail.id,
          sendType: message.sendType,
          content: message.content,
          parentId: message.parentId,
          branchId: message.branchId,
          mentionIdsArr: message.mentions,
          threadId: message.threadId,
          currentMessage: toSnake(message),
          forward: message.forward,
        },
        StorageUtil.getCurrentPrefixKey(),
      ),
    );
  };

  const handleResendMsgEncryptNotResend = async (message, groupDetail, deviceList) => {
    const messageEncrypts = await getInteractor(prefixKey).LocalMessageEncryptService.findBySourceId(message.sourceId);
    let validMessages = [];
    messageEncrypts.forEach(messageEncrypt => {
      const validMessage = {
        messageId: messageEncrypt.id,
        sendToDeviceId: messageEncrypt.send_to_device_id,
        sendToAccountId: messageEncrypt.send_to_account_id,
        content: messageEncrypt.content,
        options: messageEncrypt.options,
        status: SystemConstant.MESSAGE_STATUS.send,
        callStatus: message.callStatus,
        sendType: message.sendType,
        mentions: message.mentions,
        encryptType: SystemConstant.ENCRYPTION_TYPE.NORMAL_ENCRYPTION,
      };
      validMessages.push(validMessage);
    });
    const newMessage = {
      isSendingKey: false,
      groupId: message.groupId,
      sourceId: message.sourceId,
      groupType: groupDetail.groupType,
      sendType: message.sendType,
      created: Date.now(),
      messages: validMessages,
      branchId: message.branchId,
      parentId: message.parentId,
      threadId: message.threadId,
      messageId: message.id,
    };
    if (groupDetail.groupType !== SystemConstant.GROUP_CHAT_TYPE.personal) {
      newMessage.deviceList = deviceList.map(device => {
        return device.id;
      });
    }

    handleEncryptResend(message);

    await getInteractor(prefixKey).LocalApiCallService.save([
      {
        id: uuid(),
        task: `${AppConstant.API_CALL_TASK.message}`,
        original_uid: newMessage.sourceId,
        query: "",
        content: JSON.stringify({ data: newMessage }),
        original_content: JSON.stringify({ data: newMessage }),
        created: new Date().getTime(),
        retry: 0,
        branch_id: newMessage.branchId,
        group_id: newMessage.groupId,
      },
    ]);
    dispatch(SystemActions.executeQueue());
  };
  useEffect(() => {
    const isValid = Boolean(data && data.id && data.sourceId && data.state === SystemConstant.STATE.active);
    if (!isValid) return;

    getLatestMessage(prefixKey, data).then(latestMessage => {
      if (!isEqual(latestMessage, message) && isMounted()) {
        setMessage(latestMessage);
        setMessageOption(latestMessage?.options ? convertString2JSON(latestMessage?.options) : {});
      }
    });

    if (!isSearchMode) {
      // Handling reply message
      getThreadDetail(prefixKey, data.sourceId).then(thread => {
        if (!isEqual(thread, threadInfo) && isMounted()) setThreadInfo(thread);
      });

      // Get all reaction of origin message
      getReactionList(prefixKey, data.sourceId).then(msgReactionList => {
        if (!isEqual(msgReactionList, reactionList) && isMounted()) setReactionList(msgReactionList);
      });
    }

    if (data.sendAsDirectMessage && isViewMode) {
      countNewOriginalReplyMsg(prefixKey, data.created, data.threadId).then(numberNewReply => {
        setHasNewReply(Boolean(numberNewReply > 0));
      });
    }
  }, [data]);

  useEffect(() => {
    const isValid =
      !isSearchMode &&
      Boolean(
        message.id &&
          Array.isArray(updateMessageStatus.updateMessageIds) &&
          updateMessageStatus.updateMessageIds.includes(message.id),
      );
    if (!isValid) return;

    // Handling reply message
    getThreadDetail(prefixKey, data.sourceId).then(thread => {
      if (!isEqual(thread, threadInfo) && isMounted()) setThreadInfo(thread);
    });
  }, [updateMessageStatus]);

  useEffect(() => {
    if (data.threadId && updateThreadIds.includes(data.threadId) && data.sendAsDirectMessage && isViewMode) {
      countNewOriginalReplyMsg(prefixKey, data.created, data.threadId).then(numberNewReply => {
        setHasNewReply(Boolean(numberNewReply > 0));
      });
    }
  }, [updateThreadIds]);

  // Not show action in search mode, parent message in thread mode, editing, message is sending
  const isSendingMessage = message.senderDeviceId === deviceId && false === Boolean(message.modified);
  const isShowAction = !isSearchMode && (isThreadMode ? Boolean(message.threadId) : !isEditing) && !isSendingMessage;
  const isShowThreadReply = threadInfo.messageThreadTotal > 0 && !isThreadMode;
  const isNotExistMessage =
    message.isDeleteAll || message.isDeleteOnlyMe || message.sendType === SystemConstant.SEND_TYPE.notFound;
  const isDecrypting =
    message?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover ||
    message?.messageEncryptStatus === SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed;

  if (!Boolean(message.id && message.sourceId)) return null;
  return (
    <>
      <Box
        className={clsx(classes.chatItemRoot, isMine && classes.chatItemRootFromMe, isHiddenMessage && "hidden")}
        onMouseEnter={handleHoverChat(true)}
        onMouseLeave={handleHoverChat(false)}
        {...otherProps}
      >
        {isShowAction && (
          <ChatAction
            isShow={isHovered}
            isShowMessageDetail={isShowMessageDetail}
            isThreadMode={isThreadMode}
            message={message}
            reactionList={reactionList}
            onEditMessage={handleEditMode}
            isThreadSetting={isShowThreadReply}
            isSendErr={sendingMsgStatus === SENDING_STATUS.sendErr}
          />
        )}
        {isSendErr && (
          <Stack direction="row" alignItems="center">
            <Box className={classes.chatReSend} onClick={handleResendMessage}>
              <IconButton color="primary" className={classes.chatReSendButton}>
                <Refresh style={{ fontSize: 12 }} />
              </IconButton>
              <Typography color="primary" className={classes.chatReSendText}>
                {getLabel(LangConstant.TXT_RESEND)}
              </Typography>
            </Box>
          </Stack>
        )}

        <Stack
          direction="row"
          justifyContent={isMine ? "flex-end" : "flex-start"}
          className={clsx(classes.messageContent, message.isDisappear && !isFocused && "disappear-opacity")}
        >
          {isFailedSending && (
            <IconButton color="error" className={classes.chatMenuButton}>
              <Replay />
            </IconButton>
          )}

          {isNotExistMessage ? (
            <DeleteMessage message={message} />
          ) : [SystemConstant.SEND_TYPE.image, SystemConstant.SEND_TYPE.video].includes(message.sendType) &&
            !isDecrypting &&
            isJSONString(message.content) ? (
            <ImageMessage message={message} isMine={isMine} />
          ) : message.sendType === SystemConstant.SEND_TYPE.file && !isDecrypting ? (
            <FilePreview message={message} />
          ) : message.sendType === SystemConstant.SEND_TYPE.quote &&
            // Nếu là tin nhắn quote nhưng không phải là tin nhắn đã được giải mã thì hiển thị theo logic MessengerPeople
            !isDecrypting ? (
            <QuoteMessage message={message} isEditing={isEditing} onCloseEdit={handleCloseEdit} />
          ) : (
            <MessengerPeople
              message={message}
              isMine={isMine}
              isEditing={isEditing}
              onCloseEdit={handleCloseEdit}
              isDecryptFailed={isDecryptFailed}
              setIsDecryptFailed={setIsDecryptFailed}
            />
          )}
        </Stack>
      </Box>

      {!(isEditing || isSearchMode || isHiddenMessage) && (
        <Stack
          width="100%"
          mt={0.5}
          spacing={0.5}
          className={isMine ? classes.myNewReplyBtn : classes.otherNewReplyBtn}
        >
          {reactionList.length > 0 && <ReactionFlow reactionList={reactionList} message={message} />}

          {isShowThreadReply && <ThreadFlow parentMessageId={message.sourceId} threadDetail={threadInfo} />}
        </Stack>
      )}

      {!isHiddenMessage && message.sendAsDirectMessage && isViewMode && hasNewReply && (
        <Stack width="100%" className={isMine ? classes.myNewReplyBtn : classes.otherNewReplyBtn}>
          <NewReplyButton onClick={handleJump2Thread}>
            {getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_VIEW_NEW_REPLY")}
          </NewReplyButton>
        </Stack>
      )}

      {!isHiddenMessage && (
        <SendingStatus
          isHovered={isHovered}
          sendingMsgStatus={sendingMsgStatus}
          messageSourceId={message.sourceId}
          isMine={isMine}
          messageOption={messageOption}
        />
      )}
    </>
  );
};

export default memo(ChatItem);

const SENDING_STATUS = {
  sending: "is_sending_message",
  failed: "failed_sending_message",
  success: "success_sending_message",
  sendErr: "send_error_message",
};
const SendingStatus = ({ sendingMsgStatus, isHovered, isMine, messageSourceId, messageOption, ...otherProps }) => {
  const classes = useStyles();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);

  const [msgLabelKey, setMsgLabelKey] = useState(""); // Sent-Received-Seen

  useEffect(() => {
    let isMounted = true;
    if (msgLabelKey === LangConstant.TXT_SEEN) return;

    if ([SENDING_STATUS.sending, SENDING_STATUS.failed, SENDING_STATUS.sendErr].includes(sendingMsgStatus)) {
      setMsgLabelKey("");
    } else if (isMine) {
      getMyMessageStatus(messageSourceId).then(messageLabelKey => {
        if (isMounted) setMsgLabelKey(messageLabelKey);
      });
    } else if (!isMine) {
      setMsgLabelKey(LangConstant.TXT_SEEN);
    }
    return () => {
      isMounted = false;
    };
  }, [sendingMsgStatus, isHovered]);

  const getColorCode = type => {
    switch (type) {
      case EncryptTypeProtocol.MKV:
        return "#15B500";
      case EncryptTypeProtocol.MKC:
        return "red";
      case EncryptTypeProtocol.AES:
        return "#0033A0";
      default:
        return "#0033A0"; // Default color
    }
  };

  return (
    <Box mt={1} {...otherProps}>
      {SENDING_STATUS.sending === sendingMsgStatus && (
        <Typography
          className={clsx(classes.sentStatus, {
            [classes.otherSentStatus]: !isMine,
          })}
        >
          {getLabel(LangConstant.TXT_SENDING)}
          <LockIcon fill={getColorCode(messageOption?.encrypt_version)} />
        </Typography>
      )}
      {SENDING_STATUS.failed === sendingMsgStatus && (
        <Typography
          className={clsx(classes.sentStatus, classes.failedStatus, {
            [classes.otherSentStatus]: !isMine,
          })}
        >
          {getLabel(LangConstant.TXT_SEND_FAILED)} <Error />
          <LockIcon fill={getColorCode(messageOption?.encrypt_version)} />
        </Typography>
      )}
      {SENDING_STATUS.sendErr === sendingMsgStatus && (
        <Typography
          className={clsx(classes.sentStatus, classes.failedStatus, {
            [classes.otherSentStatus]: !isMine,
          })}
        >
          {getLabel(LangConstant.TXT_SEND_FAILED)}
        </Typography>
      )}

      {msgLabelKey && (
        <Typography
          className={clsx(classes.sentStatus, !isHovered && classes.sentStatusHidden, {
            [classes.otherSentStatus]: !isMine,
          })}
        >
          {getNSLang(LangConstant.NS_HOME_CONVERSATION, msgLabelKey)}
          <LockIcon fill={getColorCode(messageOption?.encrypt_version)} />
        </Typography>
      )}
    </Box>
  );
};

const getReactionList = async (prefixKey, parentMessageId) => {
  try {
    const records = await getInteractor(prefixKey).LocalMessageService.getChildMessages(parentMessageId, [
      SystemConstant.SEND_TYPE.reaction,
      SystemConstant.SEND_TYPE.deleteReaction,
    ]);

    const msgReactionList = toCamel(records).sort((current, prev) => current.created - prev.created);
    const handleReactionObj = msgReactionList.reduce((newReactList, react) => {
      const account = react.senderId;
      newReactList[account] = (newReactList[account] || []).concat(react);

      return newReactList;
    }, {});
    const finalReactionArr = Object.values(handleReactionObj)
      .map(data => (data.length > 0 ? data[data.length - 1] : data[0]))
      .filter(react => react.sendType === SystemConstant.SEND_TYPE.reaction);

    return finalReactionArr;
  } catch (error) {
    console.error(error);
  }

  return [];
};

const getThreadDetail = async (prefixKey, threadId) => {
  let threadDetail = { messageThreadTotal: 0, unreadMsgThreadTotal: 0, accountReply: [], lastReplyTime: 0 };
  if (threadId) {
    try {
      const threadInfo = await getInteractor(prefixKey).LocalThreadService.getByThreadId(threadId);

      if (threadInfo) {
        threadDetail.messageThreadTotal = threadInfo.total_reply || 0;
        threadDetail.unreadMsgThreadTotal = threadInfo.total_unread || 0;
        threadDetail.accountReply = threadInfo.account_reply ? JSON.parse(threadInfo.account_reply) : [];
        threadDetail.lastReplyTime = threadInfo.modified || threadInfo.created;
      }
    } catch (error) {
      console.error(error);
    }
  }

  return threadDetail;
};

const getMyMessageStatus = async messageSourceId => {
  const [seenMemberCount, receivedMemberCount] = await Promise.all([
    getInteractor().LocalSeenMemberService.count({ source_id: messageSourceId }),
    getInteractor().LocalReceivedMemberService.count({
      source_id: messageSourceId,
    }),
  ]);

  return seenMemberCount > 0
    ? LangConstant.TXT_SEEN
    : receivedMemberCount > 0
      ? LangConstant.TXT_RECEIVED
      : LangConstant.TXT_SENT;
};

const countNewOriginalReplyMsg = async (prefixKey, timestamp, threadId) => {
  try {
    const numberNewReply = await getInteractor(prefixKey).LocalMessageService.count(
      {
        thread_id: threadId,
        send_type: SystemConstant.DISPLAY_MESSAGE_TYPES,
        state: SystemConstant.STATE.active,
      },
      `AND (parent_id is NULL OR parent_id = '') AND message.created > ${timestamp}`,
    );

    return numberNewReply;
  } catch (error) {
    console.error(error);
  }

  return 0;
};

const NewReplyButton = styled(Button)({
  textTransform: "none",
  fontSize: 12,
  marginTop: 4,
  padding: 0,
  width: "fit-content",
});

const useStyles = makeStyles({
  chatItemRoot: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    paddingTop: 8,
    paddingBottom: 0,
  },

  chatItemRootFromMe: {
    flexDirection: "row",
  },

  messageContent: {
    maxWidth: "calc(90% - 80px)",
    cursor: "pointer",
  },

  chatMenuButton: {
    width: 24,
    height: 24,
    minHeight: 10,
    minWidth: 10,
    padding: 4,
  },

  extraInfo: {
    position: "absolute",
    left: 80,
    display: "flex",
  },

  extraInfoMine: {
    position: "absolute",
    right: 30,
    display: "flex",
  },

  messageWithExtra: {
    marginBottom: 10,
  },

  sentStatus: {
    textAlign: "end",
    padding: "0 28px",
    fontSize: "0.5625rem",
    fontWeight: 450,
    opacity: 1,
    transition: "opacity 0.2s ease-in-out",
  },

  otherSentStatus: {
    textAlign: "start",
    paddingLeft: 60,
  },

  sentStatusHidden: {
    opacity: 0,
  },

  failedStatus: {
    fontSize: "0.75rem",
    fontWeight: 400,
    color: "red",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0 8px",

    "&>svg": {
      fontSize: "1.125rem",
      marginLeft: 2,
    },
  },

  myNewReplyBtn: {
    alignItems: "end",
  },

  otherNewReplyBtn: {
    paddingLeft: 56,
    justifyContent: "start",
  },

  chatReSendButton: {
    padding: 0,
    lineHeight: "20px",
    marginRight: 2,
  },
  chatReSendText: {
    fontSize: 12,
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
  chatReSend: {
    height: "20px",
    display: "flex",
    lineHeight: "20px",
    cursor: "pointer",
    marginRight: 8,
  },
});
